/**
 * Normalise Azure Static Web Apps / Entra clientPrincipal into a compact
 * identity payload suitable for API headers and activity logging.
 *
 * SWA `/.auth/me` returns claims as `{ typ, val }`. Entra groups usually
 * appear as GUIDs unless the app registration emits group names.
 */

const CLAIM_MAP = {
    name: [
        'name',
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'
    ],
    email: [
        'email',
        'emails',
        'preferred_username',
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
    ],
    oid: [
        'oid',
        'http://schemas.microsoft.com/identity/claims/objectidentifier'
    ],
    tid: [
        'tid',
        'http://schemas.microsoft.com/identity/claims/tenantid'
    ],
    groups: [
        'groups',
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/groups'
    ],
    roles: [
        'roles',
        'role',
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
    ],
    givenName: [
        'given_name',
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'
    ],
    familyName: [
        'family_name',
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'
    ],
    upn: [
        'upn',
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/upn'
    ]
};

const asArray = (value) => {
    if (value == null || value === '') {
        return [];
    }
    return Array.isArray(value) ? value : [value];
};

const unique = (values) => [...new Set(values.filter((value) => value != null && value !== ''))];

/**
 * @param {Array<{typ?: string, type?: string, val?: string, value?: string}>} claims
 * @returns {Record<string, string[]>}
 */
export const indexClaims = (claims = []) => {
    const indexed = {};
    if (!Array.isArray(claims)) {
        return indexed;
    }

    claims.forEach((claim) => {
        const type = claim?.typ || claim?.type;
        const value = claim?.val ?? claim?.value;
        if (!type || value == null || value === '') {
            return;
        }
        if (!indexed[type]) {
            indexed[type] = [];
        }
        indexed[type].push(String(value));
    });

    return indexed;
};

const firstClaim = (indexed, aliases) => {
    for (const alias of aliases) {
        const values = indexed[alias];
        if (values && values.length) {
            return values[0];
        }
    }
    return null;
};

const allClaims = (indexed, aliases) => {
    const collected = [];
    aliases.forEach((alias) => {
        collected.push(...asArray(indexed[alias]));
    });
    return unique(collected);
};

/**
 * Build a compact, queryable user identity object from a SWA clientPrincipal.
 * @param {object|null} principal
 * @returns {object|null}
 */
export const buildUserContext = (principal) => {
    if (!principal) {
        return null;
    }

    const indexed = indexClaims(principal.claims);
    const groups = allClaims(indexed, CLAIM_MAP.groups);
    const appRoles = unique([
        ...asArray(principal.userRoles),
        ...allClaims(indexed, CLAIM_MAP.roles)
    ]);

    return {
        userId: principal.userId || firstClaim(indexed, CLAIM_MAP.oid) || null,
        userDetails: principal.userDetails || null,
        email: firstClaim(indexed, CLAIM_MAP.email) || principal.userDetails || null,
        name: firstClaim(indexed, CLAIM_MAP.name) || null,
        givenName: firstClaim(indexed, CLAIM_MAP.givenName) || null,
        familyName: firstClaim(indexed, CLAIM_MAP.familyName) || null,
        upn: firstClaim(indexed, CLAIM_MAP.upn) || null,
        objectId: firstClaim(indexed, CLAIM_MAP.oid) || null,
        tenantId: firstClaim(indexed, CLAIM_MAP.tid) || null,
        identityProvider: principal.identityProvider || null,
        roles: appRoles,
        groups
    };
};

/**
 * UTF-8 safe base64 so APIM/Functions can decode X-User-Context.
 * @param {string} value
 * @returns {string}
 */
export const utf8ToBase64 = (value) => {
    if (typeof TextEncoder === 'function') {
        const bytes = new TextEncoder().encode(value);
        let binary = '';
        bytes.forEach((byte) => {
            binary += String.fromCharCode(byte);
        });
        return btoa(binary);
    }

    return btoa(
        encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_, hex) =>
            String.fromCharCode(parseInt(hex, 16))
        )
    );
};

/**
 * Encode the compact identity object for a single request header.
 * @param {object|null} context
 * @returns {string|null}
 */
export const encodeUserContextHeader = (context) => {
    if (!context) {
        return null;
    }
    return utf8ToBase64(JSON.stringify(context));
};

/**
 * Headers to attach to every API request. Keep this to one extra header so
 * APIM CORS only needs to allow `X-User-Context` in addition to existing ones.
 * @param {object|null} context
 * @returns {Record<string, string>}
 */
export const buildIdentityHeaders = (context) => {
    const encoded = encodeUserContextHeader(context);
    if (!encoded) {
        return {};
    }
    return {
        'X-User-Context': encoded
    };
};
